import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatSecondsToReadable } from '@/util/dateUtils';
// Time options in seconds
export const REMINDER_TIME_OPTIONS = [
  { value: 10 },
  { value: 30 },
  { value: 60 },
  { value: 180 },
  { value: 300 },
  { value: 600 },
  { value: 900 },
  { value: 1800 },
  { value: 3600 },
  { value: 7200 },
  { value: 14400 },
  { value: 86400 },
] as const;

export const REMINDER_CONSTRAINTS = {
  DEFAULT_SECONDS: 300,
  MIN_SECONDS: 10,
  MAX_SECONDS: 86400,
  MAX_REMINDERS: 3,
} as const;

interface ReminderTimeSelectProps {
  value?: number | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  disabledOptions?: number[];
}

export const ReminderTimeSelect = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  placeholder,
  disabledOptions = [],
}: ReminderTimeSelectProps) => {
  // Ensure value is a number for comparison
  const numericValue = typeof value === 'string' ? parseInt(value, 10) : value;

  // Check if current value is in the predefined options
  const isCustomValue =
    numericValue && !REMINDER_TIME_OPTIONS.some((opt) => opt.value === numericValue);

  // Get display value - either from options or formatted custom value
  const getDisplayValue = () => {
    if (!numericValue) {
      return undefined;
    }

    return formatSecondsToReadable(numericValue);
  };

  return (
    <Select
      value={numericValue ? numericValue.toString() : ''}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger className={cn('h-10', { 'border-destructive': hasError })}>
        <SelectValue placeholder={placeholder}>{getDisplayValue()}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {/* Show custom value as first option if it exists and is not in predefined list */}
        {isCustomValue && (
          <SelectItem value={numericValue.toString()}>
            {formatSecondsToReadable(numericValue)} (custom)
          </SelectItem>
        )}
        {REMINDER_TIME_OPTIONS.map(({ value: optValue }) => {
          const isDisabled = disabledOptions.includes(optValue);
          return (
            <SelectItem key={optValue} value={optValue.toString()} disabled={isDisabled}>
              {formatSecondsToReadable(optValue)}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
